import { dropinApi, httpClient } from "@bw/shared/api";
import type {
  FacilityDropInCheckInMode,
  FacilityResponse,
} from "@bw/shared/types";
import type {
  OpenPlayCancellationPolicyConfigurationResponse,
  OpenPlayCancellationPolicyCreate,
  OpenPlayCancellationPolicyPreviewResponse,
  OpenPlayCancellationPolicyResponse,
} from "@bw/shared/types/generated";

// Drop-in client — wraps the facility check-in-mode endpoint. The FE owns NO
// business logic: the backend enforces the staff-check-in constraint
// (staff/hybrid require staff_check_in_available) and pricing. This client only
// forwards values and surfaces backend 4xx errors.
//
// Backend contract (backend/app/routers/facility.py — router prefix "/facilities"):
//   PATCH /facilities/{facility_id}  body FacilityUpdate{drop_in_check_in_mode}
//         -> FacilityResponse               (check-in mode lives ON THE FACILITY)
//
// The former drop-in "schedule" table (GET /drop-in-schedules,
// PUT /courts/{court_id}/drop-in-schedule) was retired in Slice 1 of the
// unified-pricing initiative: an open_play pricing rule (days/time/price, set in
// the Rates tab) now IS the drop-in schedule + price.

export type CheckInMode = FacilityDropInCheckInMode;

// The exact, real backend enum (FACILITY_DROP_IN_CHECKIN_MODES in
// backend/common/models.py). Kept as the FULL set (including the retired
// "hybrid") so a facility that still has it stored reads its true value back;
// SELECTABLE_CHECK_IN_MODES below is the UI-only subset offered as a NEW choice.
export const DROP_IN_CHECK_IN_MODES = [
  "self_check_in",
  "staff_check_in",
  "hybrid",
] as const satisfies readonly CheckInMode[];

// The two modes that actually carry behavior (staff QR-validation gate).
// "hybrid" is retired — the backend treats it as self_check_in at runtime and it
// is no longer offered as a choice, though a facility that already has it stored
// still reads it back via DROP_IN_CHECK_IN_MODES above.
export const SELECTABLE_CHECK_IN_MODES = [
  "self_check_in",
  "staff_check_in",
] as const satisfies readonly CheckInMode[];

export function updateCheckInMode(
  facilityId: string,
  mode: CheckInMode,
): Promise<FacilityResponse> {
  return httpClient.patch<FacilityResponse>(`/facilities/${facilityId}`, {
    drop_in_check_in_mode: mode,
  });
}

export function getOpenPlayCancellationPolicyConfiguration(
  facilityId: string,
): Promise<OpenPlayCancellationPolicyConfigurationResponse> {
  return dropinApi.getCancellationPolicy(facilityId);
}

export function previewOpenPlayCancellationPolicy(
  facilityId: string,
  data: OpenPlayCancellationPolicyCreate,
): Promise<OpenPlayCancellationPolicyPreviewResponse> {
  return dropinApi.previewCancellationPolicy(facilityId, data);
}

export function publishOpenPlayCancellationPolicy(
  facilityId: string,
  data: OpenPlayCancellationPolicyCreate,
): Promise<OpenPlayCancellationPolicyResponse> {
  return dropinApi.publishCancellationPolicy(facilityId, data);
}
